import type { DirectoryHandle, FileHandle } from "idfweb-project";

/**
 * The files of the projects saved in this browser, in the site's origin
 * private file system (OPFS): `projects/<key>/<tree>/`, where `<tree>` is the
 * name the tree has in the guest. The runtime worker keeps one of them in step
 * with the guest through `DirectorySync`, as it does a folder on the user's
 * computer. The list of saved projects is in `saved-projects.ts`.
 */

const PROJECTS = "projects";

/** Whether this browser has an origin private file system. */
export function browserStorageSupported(): boolean {
    return typeof navigator.storage?.getDirectory === "function";
}

/**
 * Why the origin private file system cannot be used, or undefined when it
 * can. A private window refuses it: Safari's at once on the page, while in
 * its workers the call never settles, so the page asks before the worker does.
 */
export async function storageRefused(): Promise<string | undefined> {
    try {
        await storageRoot();
        return undefined;
    } catch (e) {
        return e instanceof Error ? e.message : String(e);
    }
}

/** The saved tree under `key`, created with `create`, or undefined when there is none. */
export async function savedTree(key: string, tree: string, create: boolean): Promise<DirectoryHandle | undefined> {
    try {
        const projects = await (await storageRoot()).getDirectoryHandle(PROJECTS, { create });
        const saved = await projects.getDirectoryHandle(key, { create });
        return writable(await saved.getDirectoryHandle(tree, { create }));
    } catch (e) {
        if (e instanceof DOMException && e.name === "NotFoundError") return undefined;
        throw e;
    }
}

export async function removeSavedTree(key: string): Promise<void> {
    try {
        const projects = await (await storageRoot()).getDirectoryHandle(PROJECTS);
        await projects.removeEntry(key, { recursive: true });
    } catch (e) {
        if (!(e instanceof DOMException && e.name === "NotFoundError")) throw e;
    }
}

/** The bytes of the files saved under `key`, or undefined when there are none. */
export async function savedTreeBytes(key: string): Promise<number | undefined> {
    let saved: FileSystemDirectoryHandle;
    try {
        saved = await (await (await storageRoot()).getDirectoryHandle(PROJECTS)).getDirectoryHandle(key);
    } catch (e) {
        if (e instanceof DOMException && e.name === "NotFoundError") return undefined;
        throw e;
    }
    return treeBytes(saved);
}

async function treeBytes(dir: FileSystemDirectoryHandle): Promise<number> {
    let total = 0;
    for await (const [, entry] of (dir as unknown as DirectoryEntries).entries()) {
        total += entry.kind === "directory" ? await treeBytes(entry) : (await entry.getFile()).size;
    }
    return total;
}

/** How long the file system may take to open before it is taken to be refused. */
const OPEN_TIMEOUT_MS = 10_000;

function storageRoot(): Promise<FileSystemDirectoryHandle> {
    return Promise.race([
        navigator.storage.getDirectory(),
        new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("the browser's storage did not open")), OPEN_TIMEOUT_MS),
        ),
    ]);
}

/** The bytes this site may still store, or undefined when the browser does not say. */
export async function storageRoom(): Promise<number | undefined> {
    const estimate = await navigator.storage.estimate?.();
    if (estimate?.quota === undefined || estimate.usage === undefined) return undefined;
    return estimate.quota - estimate.usage;
}

/**
 * The handle, with `createWritable` made from a sync access handle where the
 * browser has only that: Safari before 26 writes OPFS files from a worker
 * only this way.
 */
function writable(dir: FileSystemDirectoryHandle): DirectoryHandle {
    const handle = dir as unknown as DirectoryHandle;
    if (typeof FileSystemFileHandle === "undefined" || "createWritable" in FileSystemFileHandle.prototype) {
        return handle;
    }
    return wrapDirectory(dir);
}

function wrapDirectory(dir: FileSystemDirectoryHandle): DirectoryHandle {
    return {
        name: dir.name,
        async *entries() {
            for await (const [name, entry] of (dir as unknown as DirectoryEntries).entries()) {
                yield [name, entry.kind === "directory" ? wrapDirectory(entry) : wrapFile(entry)];
            }
        },
        getDirectoryHandle: async (name, options) => wrapDirectory(await dir.getDirectoryHandle(name, options)),
        getFileHandle: async (name, options) => wrapFile(await dir.getFileHandle(name, options)),
        removeEntry: (name, options) => dir.removeEntry(name, options),
    };
}

function wrapFile(file: FileSystemFileHandle): FileHandle {
    return {
        name: file.name,
        getFile: () => file.getFile(),
        createWritable: async () => {
            const access = await (file as unknown as SyncAccess).createSyncAccessHandle();
            access.truncate(0);
            let at = 0;
            return {
                write: async (data: Uint8Array) => {
                    at += access.write(data, { at });
                },
                close: async () => {
                    access.flush();
                    access.close();
                },
            };
        },
    };
}

type DirectoryEntries = {
    entries(): AsyncIterable<[string, FileSystemDirectoryHandle | FileSystemFileHandle]>;
};

/** `createSyncAccessHandle`, which only dedicated workers have. */
type SyncAccess = {
    createSyncAccessHandle(): Promise<{
        truncate(size: number): void;
        write(data: Uint8Array, options: { at: number }): number;
        flush(): void;
        close(): void;
    }>;
};
